import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Form, Row, Spinner } from 'react-bootstrap';
import { CampaignsService } from '../CampaignsService';
import { Campaign } from '../../../model/campaign/Campaign';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import SingleSelectDropdown, { ISingleSelectOption } from '../../../shared/components/SingleSelectDropdown';
import { CAMPAIGN_BUDGET_DOWNLOAD_URL } from '../../../utils/Endpoints';
import { FaTimes } from 'react-icons/fa';
import { ErrorToast } from '../../../utils/toasters';
import { PackagesService } from '../../packages/PackagesService';
import { IPackages } from '../../../model/package/IPackage';
import { getCorrectEnums } from '../../../utils/Functions';
import { downloadFileRequest } from '../../../api/api.service';

interface IOfferFilter {
    handleState: (...arg: any) => void;
    setState: (...arg: any) => void;
    budgetFilter: any;
}

export function BudgetOverviewFilter({ handleState, setState, budgetFilter }: IOfferFilter) {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignFilter, setCampaignFilter] = useState('');
    const [inputTypes, setInputTypes] = useState({
        from: 'text',
        to: 'text',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [packagesList, setPackagesList] = useState<IPackages[]>([]);

    useEffect(() => {
        CampaignsService.getCampaigns({
            perPage: 15,
            title: campaignFilter,
            isLongTerm: false,
            approvalStatus: 'approved',
        })
            .then(data => {
                setCampaigns(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [campaignFilter]);

    useEffect(() => {
        PackagesService.getPackages({ page: 1, perPage: 99999999 })
            .then(data => {
                setPackagesList(data.data);
            })
            .catch(err => ErrorToast(err));
    }, []);

    function handleChanges(evt: any) {
        const value = evt.target.value;
        setState({
            ...budgetFilter,
            [evt.target.name]: value,
        });
    }

    const renderSelect = (name: string, placeholder: string, options: ISingleSelectOption[], emptyValue = '') => {
        const current = budgetFilter?.[name];
        return (
            <SingleSelectDropdown
                placeholder={placeholder}
                options={options}
                selected={current === undefined || String(current) === emptyValue ? '' : String(current)}
                onChange={value => handleChanges({ target: { name, value: value || emptyValue } })}
            />
        );
    };

    function handleTypes(evt: any, type: string) {
        setInputTypes(prev => ({
            ...prev,
            [evt.target.name]: type,
        }));
    }

    return (
        <Form>
            <Row style={{ rowGap: '18px' }}>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Campaign title or id"
                        onSelect={setState}
                        setFilter={setCampaignFilter}
                        keyState={'id'}
                        keyItemSelect={'id'}
                        value={campaignFilter}
                        items={campaigns}
                        hasClearBtn
                        renderItems={['id', 'title']}
                        clearFunc={() => {
                            setState((prev: any) => ({ ...prev, id: undefined }));
                            setCampaignFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect('hasApplicationDeadlinePassed', 'Application deadline?', [
                        { value: 'false', label: 'Passed deadline' },
                        { value: 'true', label: 'Not Passed deadline' },
                    ])}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect('hasSpending', 'Spending?', [
                        { value: 'true', label: 'Has spending' },
                        { value: 'false', label: 'No spending' },
                    ])}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'packageId',
                        'All Packages',
                        packagesList.map((item, i) => ({
                            value: String(i * 500),
                            label: getCorrectEnums(item.displayName),
                        }))
                    )}
                </div>
                <div className="col-md-2 col-6">
                    <div className="d-flex bg-secondary-o-100 border rounded">
                        <Form.Control
                            placeholder="Deadline from:"
                            className="autocomplete-input border-0 cursor-pointer"
                            type={inputTypes.from}
                            name="from"
                            value={budgetFilter?.from || ''}
                            onFocus={e => handleTypes(e, 'date')}
                            onBlur={e => handleTypes(e, 'text')}
                            onChange={handleChanges}
                        />
                        {budgetFilter?.from && (
                            <Button
                                variant=""
                                className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                onClick={() => {
                                    setState((prev: any) => ({ ...prev, from: undefined }));
                                }}
                            >
                                <FaTimes fontSize={14} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="d-flex bg-secondary-o-100 border rounded">
                        <Form.Control
                            placeholder="Deadline to:"
                            className="autocomplete-input border-0 cursor-pointer"
                            type={inputTypes.to}
                            name="to"
                            value={budgetFilter?.to || ''}
                            onFocus={e => handleTypes(e, 'date')}
                            onBlur={e => handleTypes(e, 'text')}
                            onChange={handleChanges}
                        />
                        {budgetFilter?.to && (
                            <Button
                                variant=""
                                className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                onClick={() => {
                                    setState((prev: any) => ({ ...prev, to: undefined }));
                                }}
                            >
                                <FaTimes fontSize={14} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'hasSupervisor',
                        'Has supervisor?',
                        [
                            { value: 'true', label: 'Has supervisor' },
                            { value: 'false', label: 'No supervisor' },
                        ],
                        'undefined'
                    )}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect('hasDifference', 'Has difference?', [
                        { value: 'true', label: 'Has difference' },
                        { value: 'false', label: 'No difference' },
                    ])}
                </div>
            </Row>
            <div className="d-flex gap-2">
                <Button className="mt-4" disabled={isLoading} onClick={() => handleState(budgetFilter, 1)}>
                    Search
                </Button>
                <Button
                    className="mt-4"
                    disabled={isLoading}
                    variant="outline-primary"
                    onClick={() =>
                        downloadFileRequest(
                            CAMPAIGN_BUDGET_DOWNLOAD_URL,
                            'export-budget',
                            null,
                            budgetFilter,
                            setIsLoading
                        )
                    }
                >
                    {isLoading ? (
                        <>
                            Generating excel... <Spinner animation="border" size="sm" role="status" className="ml-1" />
                        </>
                    ) : (
                        'Export current'
                    )}
                </Button>
            </div>
        </Form>
    );
}
